import type { NewToastMessage, ToastMessage } from "@winamp/toast/schema.ts";
import { nanoid } from "nanoid";
import { useEffect, useMemo, useState } from "react";
import { Toast } from "./Toast";
import { ToastContext } from "./use-toast-context";

interface IToastContextProviderProps {
  children: React.ReactNode;
  serverToast?: ToastMessage;
}

export function ToastContextProvider({
  children,
  serverToast,
}: IToastContextProviderProps) {
  const [toasts, setToasts] = useState<Array<ToastMessage>>([]);

  function addToast(toast: NewToastMessage) {
    setToasts((toasts) => [...toasts, { ...toast, id: toast.id ?? nanoid() }]);
  }

  function removeToast(id: string) {
    setToasts((toasts) => toasts.filter((toast) => toast.id !== id));
  }

  // biome-ignore lint/correctness/useExhaustiveDependencies: This effect should only run when the serverToast change
  useEffect(() => {
    if (serverToast) {
      addToast(serverToast);
    }
  }, [serverToast]);

  const context = useMemo(
    () => ({ toasts, addToast, removeToast }),
    [toasts, addToast, removeToast],
  );

  return (
    <ToastContext.Provider value={context}>
      {children}
      <div
        aria-live="assertive"
        className="pointer-events-none fixed inset-4 z-[9999] lg:inset-12"
      >
        {toasts.length > 0 && (
          <div className="absolute top-0 right-0 w-full space-y-4 lg:max-w-80">
            {toasts.map((toast) => (
              <Toast key={toast.id} removeToast={removeToast} {...toast} />
            ))}
          </div>
        )}
      </div>
    </ToastContext.Provider>
  );
}
